/**
 * Protocol amendments panel (2026-09-28) — Study Workspace Hub.
 *
 * Lists every protocol version with its approval state, lets an
 * authorised uploader submit a new amendment (file + amendment details +
 * impact assessment), lets the study PI approve (Part-11 re-auth) or
 * reject, and shows the impact review checklist with per-task
 * acknowledgement.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, ClipboardCheck, Clock, Download, FileDiff, Info, Loader2, PenLine, Plus, XCircle } from 'lucide-react';
import {
  ApiError, authReAuth, protocol,
  type ProtocolAmendmentImpact, type ProtocolAmendmentReviewTask, type ProtocolAmendmentRow,
} from '@/api/client';
import { DragDropUpload } from '@/components/ui/DragDropUpload';
import { Modal } from '@/components/ui/Modal';
import { useHasRole } from '@/components/RoleGate';
import { useAuth } from '@/contexts/AuthContext';
import { fmtDate, fmtDateTime } from '@/lib/dates';
import { guessMime, sha256OfFile, uploadWithProgress } from '@/lib/s3Upload';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';

const SUBMIT_ROLES = [ 'site_study_designer',
  'coordinator', 'regulatory_specialist', 'principal_investigator',
] as const;

type ImpactKey = Exclude<keyof ProtocolAmendmentImpact, 'other'>;

const IMPACT_QUESTIONS: Array<{ key: ImpactKey; label: string; hint: string }> = [
  { key: 'schedule_of_assessments', label: 'Schedule of assessments / visit windows', hint: 'Visits added, removed, re-timed or procedures changed.' },
  { key: 'esource_forms', label: 'eSource / eCRF forms', hint: 'Fields or forms that must change to capture the amended protocol.' },
  { key: 'doa_responsibilities', label: 'Delegation of Authority responsibilities', hint: 'Tasks that must be re-delegated or newly delegated.' },
  { key: 'informed_consent', label: 'Informed consent', hint: 'Risks, procedures or wording that require a new ICF and re-consent.' },
  { key: 'eligibility', label: 'Eligibility criteria', hint: 'Inclusion / exclusion criteria changed.' },
];

const EMPTY_IMPACT: ProtocolAmendmentImpact = {
  schedule_of_assessments: { changed: false, note: '' },
  esource_forms: { changed: false, note: '' },
  doa_responsibilities: { changed: false, note: '' },
  informed_consent: { changed: false, note: '' },
  eligibility: { changed: false, note: '' },
  other: '',
};

function statusBadge(row: ProtocolAmendmentRow) {
  if (row.approval_status === 'pending_review') {
    return { label: 'Awaiting PI approval', cls: 'bg-amber-100 text-amber-800', Icon: Clock };
  }
  if (row.approval_status === 'rejected') {
    return { label: 'Rejected', cls: 'bg-danger-100 text-danger-700', Icon: XCircle };
  }
  if (row.is_current) {
    return { label: 'Current protocol', cls: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle2 };
  }
  return { label: 'Superseded', cls: 'bg-surface-muted text-ink-secondary', Icon: FileDiff };
}

export function ProtocolAmendmentsPanel({ studyId, readOnly = false }: { studyId: string; readOnly?: boolean }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canSubmit = useHasRole(SUBMIT_ROLES) && !readOnly;
  const [addOpen, setAddOpen] = useState(false);
  const [decision, setDecision] = useState<{ row: ProtocolAmendmentRow; mode: 'approve' | 'reject' } | null>(null);
  const [ackTask, setAckTask] = useState<{ row: ProtocolAmendmentRow; task: ProtocolAmendmentReviewTask } | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['protocol-amendments', studyId],
    queryFn: () => protocol.listAmendments(studyId),
    enabled: !!studyId,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['protocol-amendments', studyId] });
    qc.invalidateQueries({ queryKey: ['protocol-versions', studyId] });
    qc.invalidateQueries({ queryKey: ['me', 'tasks', 'open'] });
  };

  const data = q.data;
  const rows = data?.amendments ?? [];
  const current = rows.find((r) => r.is_current) ?? null;
  const amendments = rows.filter((r) => !r.is_legacy || !r.is_current);

  const download = async (row: ProtocolAmendmentRow) => {
    try {
      const r = await protocol.download(studyId, row.document_id);
      window.open(r.url, '_blank', 'noopener');
    } catch (e) {
      toast.error(errorText(e, 'Download failed'));
    }
  };

  const canAck = (task: ProtocolAmendmentReviewTask) =>
    task.status === 'open' && (
      (data?.pi_user_id && user?.id === data.pi_user_id) ||
      task.assignee_user_id === user?.id ||
      (!!task.assignee_role && task.assignee_role === user?.role)
    );

  return (
    <section className="card">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-surface-border bg-surface-muted/50 px-4 py-3">
        <div className="flex items-center gap-2">
          <FileDiff className="h-4 w-4 text-brand-500" />
          <div>
            <h2 className="text-sm font-semibold text-ink-primary">Protocol amendments</h2>
            <p className="text-xs text-ink-muted">
              {current
                ? <>Current protocol <span className="font-mono">v{current.version_label}</span> · {current.file_name}</>
                : 'No protocol on file yet — upload it in Study Onboarding.'}
              {data?.pi_name ? <> · PI: {data.pi_name}</> : null}
            </p>
          </div>
        </div>
        {canSubmit && current && (
          <button
            type="button"
            className="btn-primary text-xs"
            disabled={data?.has_pending}
            title={data?.has_pending ? 'An amendment is already awaiting PI approval' : undefined}
            onClick={() => setAddOpen(true)}
          >
            <Plus className="mr-1 h-3.5 w-3.5" /> Add amendment
          </button>
        )}
      </div>

      {q.isLoading ? (
        <div className="flex items-center gap-2 p-4 text-xs text-ink-muted"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</div>
      ) : q.isError ? (
        <div className={`flex items-start gap-2 p-4 text-xs ${errorToneClass(q.error)}`}>
          {isPermissionError(q.error) ? <Info className="mt-0.5 h-3.5 w-3.5"/> : <AlertTriangle className="mt-0.5 h-3.5 w-3.5" />}
          {errorText(q.error, 'Failed to load protocol amendments.')}
        </div>
      ) : amendments.length === 0 ? (
        <div className="p-4 text-xs text-ink-muted">
          No amendments yet. {data?.requires_approval
            ? 'Once the study is active, every protocol change is submitted here and takes effect only after the PI approves it.'
            : 'While the study is still in onboarding, the protocol can be replaced directly in Study Onboarding.'}
        </div>
      ) : (
        <ul className="divide-y divide-surface-border/60">
          {amendments.map((row) => {
            const badge = statusBadge(row);
            const open = expanded === row.document_id;
            const canDecide = data?.can_decide && row.approval_status === 'pending_review';
            return (
              <li key={row.document_id} className="px-4 py-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <button type="button" className="min-w-0 flex-1 text-left" onClick={() => setExpanded(open ? null : row.document_id)}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-ink-primary">
                        {row.amendment_number ?? `Version ${row.version_label}`}
                      </span>
                      <span className="font-mono text-[11px] text-ink-muted">v{row.version_label}</span>
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${badge.cls}`}>
                        <badge.Icon className="h-3 w-3" /> {badge.label}
                      </span>
                      {row.approval_status === 'approved' && row.review_tasks.length > 0 && (
                        <span className={`rounded-full px-2 py-0.5 text-[11px] ${row.closed_at ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>
                          {row.closed_at ? 'Checklist complete' : `${row.open_review_tasks} review task${row.open_review_tasks === 1 ? '' : 's'} open`}
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 text-xs text-ink-muted">
                      {row.file_name} · {row.amendment_date ? `dated ${fmtDate(row.amendment_date)} · ` : ''}
                      submitted {fmtDateTime(row.submitted_at || row.uploaded_at)}{row.uploaded_by_name ? ` by ${row.uploaded_by_name}` : ''}
                    </div>
                  </button>
                  <div className="flex shrink-0 items-center gap-1">
                    <button type="button" className="btn-secondary text-xs" onClick={() => download(row)} title="Download file">
                      <Download className="h-3.5 w-3.5" />
                    </button>
                    {canDecide && (
                      <>
                        <button type="button" className="btn-primary text-xs" onClick={() => setDecision({ row, mode: 'approve' })}>
                          <PenLine className="mr-1 h-3.5 w-3.5" /> Approve &amp; sign
                        </button>
                        <button type="button" className="btn-secondary text-xs text-danger-700" onClick={() => setDecision({ row, mode: 'reject' })}>
                          Reject
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {open && (
                  <div className="mt-3 space-y-3 rounded border border-surface-border bg-surface-muted/30 p-3 text-xs">
                    {row.summary_of_changes && (
                      <div>
                        <div className="font-medium text-ink-secondary">Summary of changes</div>
                        <p className="whitespace-pre-wrap text-ink-primary">{row.summary_of_changes}</p>
                      </div>
                    )}
                    {row.amendment_reason && (
                      <div>
                        <div className="font-medium text-ink-secondary">Reason</div>
                        <p className="whitespace-pre-wrap text-ink-primary">{row.amendment_reason}</p>
                      </div>
                    )}
                    {row.impact && (
                      <div>
                        <div className="font-medium text-ink-secondary">Impact assessment</div>
                        <ul className="mt-1 space-y-0.5">
                          {IMPACT_QUESTIONS.map((iq) => {
                            const a = row.impact![iq.key];
                            return (
                              <li key={iq.key} className="flex gap-2">
                                <span className={a.changed ? 'text-amber-700' : 'text-ink-muted'}>{a.changed ? '● Changed' : '○ No change'}</span>
                                <span className="text-ink-primary">{iq.label}{a.changed && a.note ? ` — ${a.note}` : ''}</span>
                              </li>
                            );
                          })}
                          {row.impact.other && <li className="text-ink-primary">Other: {row.impact.other}</li>}
                        </ul>
                      </div>
                    )}
                    {row.approval_status === 'approved' && row.approved_at && (
                      <div className="text-emerald-800">
                        Approved {fmtDateTime(row.approved_at)}{row.approved_by_name ? ` by ${row.approved_by_name}` : ''} (e-signed)
                        {row.approval_notes ? ` — ${row.approval_notes}` : ''}
                      </div>
                    )}
                    {row.approval_status === 'rejected' && (
                      <div className="text-danger-700">
                        Rejected {row.rejected_at ? fmtDateTime(row.rejected_at) : ''}{row.rejected_by_name ? ` by ${row.rejected_by_name}` : ''}
                        {row.rejection_reason ? ` — ${row.rejection_reason}` : ''}
                      </div>
                    )}
                    {row.review_tasks.length > 0 && (
                      <div>
                        <div className="flex items-center gap-1 font-medium text-ink-secondary">
                          <ClipboardCheck className="h-3.5 w-3.5" /> Review checklist
                        </div>
                        <ul className="mt-1 divide-y divide-surface-border/60 rounded border border-surface-border bg-surface-card">
                          {row.review_tasks.map((t) => (
                            <li key={t.task_id} className="flex flex-wrap items-start justify-between gap-2 px-2 py-1.5">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                  {t.status === 'open'
                                    ? <Clock className="h-3.5 w-3.5 text-amber-600" />
                                    : <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
                                  <span className="font-medium text-ink-primary">{t.label}</span>
                                  <span className="text-ink-muted">· {t.assignee_name ?? (t.assignee_role ? t.assignee_role.replace(/_/g, ' ') : 'unassigned')}</span>
                                </div>
                                {t.status !== 'open' && (
                                  <div className="ml-5 text-ink-secondary">
                                    {t.outcome === 'no_change_needed' ? 'No change needed' : 'Updated'}
                                    {t.acknowledgement_note ? ` — ${t.acknowledgement_note}` : ''}
                                    {t.resolved_at ? ` (${t.resolved_by_name ?? 'acknowledged'}, ${fmtDateTime(t.resolved_at)})` : ''}
                                  </div>
                                )}
                              </div>
                              {canAck(t) && (
                                <button type="button" className="btn-secondary text-[11px]" onClick={() => setAckTask({ row, task: t })}>
                                  Acknowledge
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {addOpen && (
        <AddAmendmentDialog
          studyId={studyId}
          currentVersionLabel={current?.version_label ?? null}
          onClose={() => setAddOpen(false)}
          onSubmitted={() => { setAddOpen(false); invalidate(); }}
        />
      )}
      {decision && (
        <DecisionDialog
          studyId={studyId}
          row={decision.row}
          mode={decision.mode}
          onClose={() => setDecision(null)}
          onDone={() => { setDecision(null); invalidate(); }}
        />
      )}
      {ackTask && (
        <AcknowledgeDialog
          studyId={studyId}
          row={ackTask.row}
          task={ackTask.task}
          onClose={() => setAckTask(null)}
          onDone={() => { setAckTask(null); invalidate(); }}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

function AddAmendmentDialog({
  studyId, currentVersionLabel, onClose, onSubmitted,
}: {
  studyId: string;
  currentVersionLabel: string | null;
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [amendmentNumber, setAmendmentNumber] = useState('');
  const [amendmentDate, setAmendmentDate] = useState('');
  const [summary, setSummary] = useState('');
  const [reason, setReason] = useState('');
  const [impact, setImpact] = useState<ProtocolAmendmentImpact>(EMPTY_IMPACT);

  const missingNotes = useMemo(
    () => IMPACT_QUESTIONS.filter((iq) => impact[iq.key].changed && !impact[iq.key].note?.trim()).map((iq) => iq.label),
    [impact],
  );

  const canSubmit =
    !!file && amendmentNumber.trim().length > 0 && /^\d{4}-\d{2}-\d{2}$/.test(amendmentDate) &&
    summary.trim().length >= 10 && reason.trim().length >= 3 && missingNotes.length === 0;

  const submit = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Pick a file');
      const mime = file.type || guessMime(file.name);
      const presigned = await protocol.presign(studyId, { file_name: file.name, mime_type: mime });
      const sha = await sha256OfFile(file);
      await uploadWithProgress(presigned.upload_url, file, mime, setProgress);
      const cleanImpact: ProtocolAmendmentImpact = {
        schedule_of_assessments: { changed: impact.schedule_of_assessments.changed, note: impact.schedule_of_assessments.note?.trim() || undefined },
        esource_forms: { changed: impact.esource_forms.changed, note: impact.esource_forms.note?.trim() || undefined },
        doa_responsibilities: { changed: impact.doa_responsibilities.changed, note: impact.doa_responsibilities.note?.trim() || undefined },
        informed_consent: { changed: impact.informed_consent.changed, note: impact.informed_consent.note?.trim() || undefined },
        eligibility: { changed: impact.eligibility.changed, note: impact.eligibility.note?.trim() || undefined },
        other: impact.other?.trim() || undefined,
      };
      return protocol.submitAmendment(studyId, {
        document_id: presigned.document_id,
        s3_bucket: presigned.s3_bucket,
        s3_key: presigned.s3_key,
        file_name: file.name,
        file_size: file.size,
        mime_type: mime,
        ...(sha ? { client_sha256: sha } : {}),
        amendment_number: amendmentNumber.trim(),
        amendment_date: amendmentDate,
        summary_of_changes: summary.trim(),
        amendment_reason: reason.trim(),
        impact: cleanImpact,
      });
    },
    onSuccess: (r) => {
      toast.success(
        r.pi_assigned
          ? `Amendment submitted as v${r.version_label}. The PI has been asked to approve it.`
          : `Amendment submitted as v${r.version_label}. No PI is assigned to this study — assign one so it can be approved.`,
      );
      onSubmitted();
    },
    onError: (e) => {
      setProgress(null);
      toast.error(e instanceof ApiError ? e.message : (e as Error).message || 'Submit failed');
    },
  });

  const setArea = (key: ImpactKey, patch: Partial<{ changed: boolean; note: string }>) =>
    setImpact((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  return (
    <Modal open onOpenChange={(o) => { if (!o && !submit.isPending) onClose(); }} title="Submit protocol amendment" width={680}>
      <div className="space-y-4 text-sm">
        <p className="text-xs text-ink-muted">
          The current protocol{currentVersionLabel ? ` (v${currentVersionLabel})` : ''} stays in force until the Principal
          Investigator approves this amendment. On approval it is filed to the eISF, staff are re-trained, and a review
          task is raised for each area you flag below.
        </p>

        <DragDropUpload
          accept="application/pdf, .pdf, .docx, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          acceptHint="PDF or DOCX, max 50 MB"
          onSelect={setFile}
          currentFileName={file?.name ?? null}
          onClear={() => setFile(null)}
          disabled={submit.isPending}
        />
        {progress !== null && (
          <div className="h-1.5 w-full overflow-hidden rounded bg-surface-muted">
            <div className="h-full bg-brand-500 transition-all" style={{ width: `${progress}%` }} />
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">Amendment number *</span>
            <input className="input mt-1 w-full" value={amendmentNumber} onChange={(e) => setAmendmentNumber(e.target.value)} placeholder="e.g. Amendment 3" />
          </label>
          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">Amendment date *</span>
            <input type="date" className="input mt-1 w-full" value={amendmentDate} onChange={(e) => setAmendmentDate(e.target.value)} />
          </label>
        </div>
        <label className="block text-xs">
          <span className="font-medium text-ink-secondary">Summary of changes *</span>
          <textarea className="input mt-1 w-full" rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="What changed in the protocol (sections, procedures, dosing, visits…)" />
        </label>
        <label className="block text-xs">
          <span className="font-medium text-ink-secondary">Reason for amendment *</span>
          <textarea className="input mt-1 w-full" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why the sponsor amended the protocol" />
        </label>

        <fieldset className="rounded border border-surface-border p-3">
          <legend className="px-1 text-xs font-semibold text-ink-secondary">Impact assessment *</legend>
          <p className="mb-2 text-[11px] text-ink-muted">For each area, does this amendment change it? A note is required when you answer Yes.</p>
          <ul className="space-y-2">
            {IMPACT_QUESTIONS.map((iq) => {
              const a = impact[iq.key];
              return (
                <li key={iq.key} className="text-xs">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="font-medium text-ink-primary">{iq.label}</div>
                      <div className="text-[11px] text-ink-muted">{iq.hint}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-1">
                        <input type="radio" name={`impact-${iq.key}`} checked={!a.changed} onChange={() => setArea(iq.key, { changed: false })} /> No change
                      </label>
                      <label className="flex items-center gap-1">
                        <input type="radio" name={`impact-${iq.key}`} checked={a.changed} onChange={() => setArea(iq.key, { changed: true })} /> Changed
                      </label>
                    </div>
                  </div>
                  {a.changed && (
                    <input
                      className="input mt-1 w-full"
                      value={a.note ?? ''}
                      onChange={(e) => setArea(iq.key, { note: e.target.value })}
                      placeholder="What changed? (required)"
                    />
                  )}
                </li>
              );
            })}
          </ul>
          <label className="mt-3 block text-xs">
            <span className="font-medium text-ink-secondary">Other impact (optional)</span>
            <input className="input mt-1 w-full" value={impact.other ?? ''} onChange={(e) => setImpact((p) => ({ ...p, other: e.target.value }))} placeholder="Anything else the site must update" />
          </label>
        </fieldset>

        {missingNotes.length > 0 && (
          <p className="text-[11px] text-amber-700">Describe what changed for: {missingNotes.join(', ')}.</p>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary text-xs" onClick={onClose} disabled={submit.isPending}>Cancel</button>
          <button type="button" className="btn-primary text-xs" disabled={!canSubmit || submit.isPending} onClick={() => submit.mutate()}>
            {submit.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
            Submit for PI approval
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

function DecisionDialog({
  studyId, row, mode, onClose, onDone,
}: {
  studyId: string;
  row: ProtocolAmendmentRow;
  mode: 'approve' | 'reject';
  onClose: () => void;
  onDone: () => void;
}) {
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [notes, setNotes] = useState('');

  const mut = useMutation({
    mutationFn: async () => {
      if (mode === 'reject') {
        return protocol.rejectAmendment(studyId, row.document_id, { rejection_reason: notes.trim() });
      }
      const receipt = await authReAuth.reAuthenticate({
        password,
        totp_code: otp.trim() || undefined,
        purpose: 'approve_protocol_amendment',
      });
      return protocol.approveAmendment(studyId, row.document_id, {
        re_auth_receipt: receipt.receipt,
        re_auth_method: receipt.method,
        approval_notes: notes.trim() || undefined,
      });
    },
    onSuccess: (r) => {
      if ('review_tasks' in r) {
        toast.success(
          `Amendment approved. v${row.version_label} is now the current protocol.` +
          (r.training_assigned ? ` ${r.training_assigned} training assignment${r.training_assigned === 1 ? '' : 's'} created.` : '') +
          (r.review_tasks_created ? ` ${r.review_tasks_created} review task${r.review_tasks_created === 1 ? '' : 's'} raised.` : ''),
        );
      } else {
        toast.success('Amendment rejected. The submitter can upload a corrected version.');
      }
      onDone();
    },
    onError: (e) => toast.error(errorText(e, 'Request failed')),
  });

  const canGo = mode === 'reject' ? notes.trim().length >= 5 : password.length >= 4;

  return (
    <Modal
      open
      onOpenChange={(o) => { if (!o && !mut.isPending) onClose(); }}
      title={mode === 'approve' ? `Approve ${row.amendment_number ?? `v${row.version_label}`} — 21 CFR Part 11 e-signature` : `Reject ${row.amendment_number ?? `v${row.version_label}`}`}
      width={520}
    >
      <div className="space-y-3 text-xs">
        {mode === 'approve' ? (
          <>
            <div className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 p-2 text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Approving makes <strong>v{row.version_label}</strong> the current protocol at this site, files it to the eISF,
                re-assigns Protocol training to every delegated staff member and raises the impact review tasks.
              </span>
            </div>
            <label className="block">
              <span className="font-medium text-ink-secondary">Password *</span>
              <input type="password" autoComplete="current-password" className="input mt-1 w-full" value={password} onChange={(e) => setPassword(e.target.value)} />
            </label>
            <label className="block">
              <span className="font-medium text-ink-secondary">OTP (if TOTP-enrolled)</span>
              <input className="input mt-1 w-full font-mono" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} />
            </label>
            <label className="block">
              <span className="font-medium text-ink-secondary">Approval notes (optional)</span>
              <textarea className="input mt-1 w-full" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </label>
            <p className="text-[11px] text-ink-muted">
              Signature meaning: I have reviewed this protocol amendment and approve its implementation at this site.
            </p>
          </>
        ) : (
          <label className="block">
            <span className="font-medium text-ink-secondary">Reason for rejection *</span>
            <textarea className="input mt-1 w-full" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Tell the submitter what must change" />
          </label>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={mut.isPending}>Cancel</button>
          <button
            type="button"
            className={mode === 'approve' ? 'btn-primary' : 'btn-secondary text-danger-700'}
            disabled={!canGo || mut.isPending}
            onClick={() => mut.mutate()}
          >
            {mut.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
            {mode === 'approve' ? 'Sign & approve' : 'Reject amendment'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

function AcknowledgeDialog({
  studyId, row, task, onClose, onDone,
}: {
  studyId: string;
  row: ProtocolAmendmentRow;
  task: ProtocolAmendmentReviewTask;
  onClose: () => void;
  onDone: () => void;
}) {
  const [outcome, setOutcome] = useState<'updated' | 'no_change_needed'>('updated');
  const [note, setNote] = useState('');

  const mut = useMutation({
    mutationFn: () => protocol.acknowledgeAmendmentTask(studyId, row.document_id, task.task_id, { outcome, note: note.trim() }),
    onSuccess: (r) => {
      toast.success(r.amendment_closed_at ? 'Acknowledged. Every review task is complete — amendment closed.' : 'Acknowledged.');
      onDone();
    },
    onError: (e) => toast.error(errorText(e, 'Request failed')),
  });

  return (
    <Modal open onOpenChange={(o) => { if (!o && !mut.isPending) onClose(); }} title={task.label} width={480}>
      <div className="space-y-3 text-xs">
        <p className="text-ink-secondary">{task.message}</p>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-1">
            <input type="radio" checked={outcome === 'updated'} onChange={() => setOutcome('updated')} /> Updated
          </label>
          <label className="flex items-center gap-1">
            <input type="radio" checked={outcome === 'no_change_needed'} onChange={() => setOutcome('no_change_needed')} /> No change needed
          </label>
        </div>
        <label className="block">
          <span className="font-medium text-ink-secondary">Note *</span>
          <textarea className="input mt-1 w-full" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder={outcome === 'updated' ? 'What was updated (e.g. Schedule v3 published)' : 'Why no change is needed'} />
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={mut.isPending}>Cancel</button>
          <button type="button" className="btn-primary" disabled={note.trim().length < 3 || mut.isPending} onClick={() => mut.mutate()}>
            {mut.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
            Acknowledge
          </button>
        </div>
      </div>
    </Modal>
  );
}
